<template>
    <div data-component="OrderDetailsActionButtons"
         class="order-details-action-buttons mt-1">
        <!-- View account button-->
        <div v-if="viewAccountPath"
             class="btn-row">
            <a :href="viewAccountPath"
               data-analytics-id="Order Details / View Account"
               data-testid="OrderDetailsViewAccountButton"
               class="btn btn-default">
                <i class="fa-fw fa-solid fa-user"></i>
                {{ l('ORDER_DETAILS.ACCOUNT_BTN_TEXT') }}
            </a>
        </div>
        <div v-else
             class="btn-row">
            <button class="btn btn-default"
                    data-analytics-id="Order Details / Add Customer To Order"
                    data-testid="OrderDetailsAddCustomerToAccountButton"
                    :disabled="!isOnline"
                    @click.prevent="addCustomerToOrder">
                <i class="fa-fw fa-solid fa-user-plus"></i>
                <span>{{ l('ORDER_DETAILS.ADD_CUSTOMER_BTN_TEXT') }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import CustomerDetailsCtrl from '@/controllers/modal/CustomerDetailsCtrl';
import EventNames from '@/lib/Events/EventNames';
import { cloneDeep } from 'lodash';
import { container } from '@/container';
import { mapState } from 'vuex';

export default {
    props: {
        order: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            /**
             * If a customer account ID was added to the order during the lifecycle of this component,
             * track it here so we can use it on the "View Account" button.
             */
            attachedCustomerAccountID: null,
        };
    },

    computed: {
        ...mapState(['isOnline']),

        customerAccountID() {
            return this.attachedCustomerAccountID || this.order.customer?.account?.ID;
        },

        viewAccountPath() {
            if (this.customerAccountID) {
                return `#/accounts/${this.customerAccountID}`;
            }
            return null;
        },
    },

    methods: {
        addCustomerToOrder() {
            container.modalFactory.open(
                'customer-details',
                CustomerDetailsCtrl,
                'partials/modals/customerDetails.html',
                {
                    customer: () => cloneDeep(this.order.customer),
                    errorMsg: () => null,
                    validateForGiftAid: () => false,
                    paymentInProgress: () => false,
                }
            ).then((result) => {
                // Note: Adding a customer without an account to an order requires additional work either in the
                // CustomerFormControl.submitAndClose() or the API so is not supported at this time.
                if (!result.customer?.account?.ID) {
                    container.toastFactory.warningTop(
                        'ORDER_DETAILS.ADD_CUSTOMER_FAIL_NO_ACCOUNT',
                    );
                    return;
                }

                const spinner = container.toastFactory.spinner(
                    'ORDER_DETAILS.ADD_CUSTOMER_IN_PROGRESS',
                    result.customer.getFullName()
                );

                container.orderResource.update(
                    {
                        id: this.order.ID,
                    },
                    {
                        customerAccountID: result.customer.account.ID,
                    },
                    () => {
                        // Update the attached customer account ID so that the "View Account" button will
                        // show and link to the correct account.
                        this.attachedCustomerAccountID = result.customer.account.ID;

                        container.eventBus.broadcast(
                            EventNames.CUSTOMER_ADDED_TO_ORDER,
                            result.customer,
                        );

                        container.toastFactory.clear(spinner);
                        container.toastFactory.success(
                            'ORDER_DETAILS.ADD_CUSTOMER_SUCCESS',
                        );
                    },
                    () => {
                        container.toastFactory.clear(spinner);
                        container.toastFactory.errorTop(
                            'ORDER_DETAILS.ADD_CUSTOMER_FAIL',
                        );
                    }
                );
            });
        },
    },
};
</script>
